<template>
  <div>
    <select
      :class="{ 'is-invalid': isMissing }"
      :disabled="!canChange"
      v-show="activeEntities.length > 0 && activeEntities.length <= limit"
      class="form-select"
      :value="modelValue"
      @input="select($event.target.value)"
    >
      <option value="" v-show="!required"></option>
      <option v-for="w in activeEntities" :value="w.id">{{ w.text }}</option>
    </select>

    <div class="alert alert-warning alert-inline" role="alert" v-show="selectedEntity?.isActive === false">
      NEAKTIVNO
    </div>

    <Autocomplete
      :max="20"
      :required="required"
      ref="autoCompleteEl"
      :class="{ 'form-control': true, 'is-invalid': isMissing }"
      v-show="activeEntities.length > limit && !selectedEntity && canChange"
      @input="onQuery"
      @onSelect="select"
      :results="filteredEntities"
    />

    <div class="row" v-show="activeEntities.length > limit && !!selectedEntity" role="button" @click="clearSelection">
      <div class="col-8">{{ selectedEntity?.text }}</div>
      <div class="col-4 text-end" v-show="canChange"><button class="btn btn-sm btn-secondary">Spremeni</button></div>
    </div>

    <div class="alert alert-warning" role="alert" v-show="!activeEntities.length">
      {{ noResultsMessage ?? "Ni aktivnih." }}
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, toRef, watch } from "vue"
import { useAuth } from "../composition-api/useAuth"
import { EMPTY_GUID } from "../utils/constants"
import { EntityState } from "./EntityState"
import Autocomplete from "nabla/components/Autocomplete.js"

defineOptions({ name: "SelectEntity" })

const props = defineProps({
  apiEntity: String,
  filter: String,
  modelValue: String,
  canChange: { type: Boolean, default: true },
  noResultsMessage: String,
  limit: { type: Number, default: 20 },
  required: { type: Boolean, default: false },
  // when bound, the parent's validation (useValidation().invalid) decides the red border instead of `required`
  // (no Boolean type on purpose: an absent prop must stay undefined, not become false)
  invalid: { default: undefined },
})

// EMPTY_GUID counts as not chosen
const isMissing = computed(() =>
  props.invalid !== undefined
    ? props.invalid
    : props.required && (!props.modelValue || props.modelValue === EMPTY_GUID),
)

const emit = defineEmits(["update:modelValue", "selected"])

const { fetchAuthAsync } = useAuth()

const entities = ref([])
const autoCompleteEl = ref(null)
const autoCompleteQ = ref("")

const filter = toRef(props, "filter")

// what the user typed in the autocomplete - the list is only searched once something is typed
const onQuery = (q) => (autoCompleteQ.value = q)

const select = (s) => {
  let result = entities.value.find((e) => e?.text === s && e?.isActive === true)
  if (!result) {
    result = entities.value.find((e) => e?.id === s && e?.isActive === true)
    if (!result && s !== EMPTY_GUID) {
      return
    }
  }
  if (s !== EMPTY_GUID && s !== "") {
    autoCompleteQ.value = result.text
    emit("update:modelValue", result.id)
    emit("selected", result)
  } else {
    autoCompleteQ.value = ""
    emit("update:modelValue", "")
    emit("selected", null)
  }
}

const activeEntities = computed(() => entities.value.filter((e) => e.isActive === true))

const filteredEntities = computed(() => {
  if (autoCompleteQ.value?.length > 0) {
    return activeEntities.value
      .filter((e) => e.text?.toLowerCase().includes(autoCompleteQ.value?.toLowerCase()))
      .map((e) => e.text)
  }
  return []
})

const loadAsync = async () => {
  entities.value = await EntityState.getEntitiesAsync(fetchAuthAsync, props.apiEntity, filter.value)
  if (entities.value.length === 1) {
    emit("update:modelValue", entities.value[0].id)
    emit("selected", entities.value[0])
  }
}

const clearSelection = async () => {
  if (props.canChange) {
    autoCompleteEl.value?.setText("")
    autoCompleteQ.value = ""
    emit("update:modelValue", "")
    emit("selected", null)
  }
}

const selectedEntity = computed(() => entities.value.find((e) => e.id === props.modelValue))

watch(
  () => filter.value,
  async () => await loadAsync(),
)

onMounted(async () => loadAsync())
</script>
